// Pure decision helpers for ExtensionUpdateBanner (#478). The web app reads the extension's
// presence record (`app` = manifest version, published since 5.9.28), so it can tell an outdated
// extension apart without that extension shipping any new code.

import { isVersionAtLeast, parseVersion } from '@bt-common/notifier/version-gate';
import { CHROME_WEB_STORE_URL } from '@common-config/constants';
import type { OnlineEngine } from '@common-reducers/ConnectedEnginesSlice';

/** Oldest extension version without the #478 send-path problems. */
export const MIN_EXTENSION_VERSION = '5.9.52';

export const EXTENSION_LISTING_URL = CHROME_WEB_STORE_URL;

/** Dismissal key in the shared nudge slice (survives route changes within the session). */
export const EXTENSION_UPDATE_DISMISS_KEY = 'ext_update_web_banner';

/**
 * An online extension is outdated when it predates the wire contract (`updateRequired`, no
 * contractVersion) or reports a parseable version below the minimum. An engine that is not an
 * extension (gateway, Baileys) is never flagged, and an unparseable version alone is not enough.
 */
export function isExtensionOutdated(engine: Pick<OnlineEngine, 'engineKind' | 'updateRequired' | 'app'>): boolean {
  if (engine.engineKind !== 'extension') return false;
  if (engine.updateRequired) return true;
  return !!parseVersion(engine.app) && !isVersionAtLeast(engine.app, MIN_EXTENSION_VERSION);
}

export function shouldShowExtensionUpdate(
  engines: ReadonlyArray<Pick<OnlineEngine, 'engineKind' | 'updateRequired' | 'app'>>,
  dismissed: boolean,
  dunningVisible: boolean,
): boolean {
  if (dismissed || dunningVisible) return false;
  return engines.some(isExtensionOutdated);
}
